import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { threadAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { VoteButtons } from '../components/VoteButtons';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Card, CardContent } from '../components/ui/card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { formatDistanceToNow } from 'date-fns';
import { 
  Search, Plus, MessageSquare, Loader2, Pin, Lock, CheckCircle, 
  HelpCircle, Sparkles, Briefcase, Calendar, MessageCircle, BookOpen, MapPin
} from 'lucide-react';

const CATEGORY_ICONS = {
  general: MessageSquare,
  help: HelpCircle,
  showcase: Sparkles,
  jobs: Briefcase,
  events: Calendar,
  feedback: MessageCircle,
  resources: BookOpen,
  portland: MapPin,
};

const CATEGORY_COLORS = {
  general: 'bg-slate-100 text-slate-700',
  help: 'bg-blue-100 text-blue-700',
  showcase: 'bg-purple-100 text-purple-700',
  jobs: 'bg-green-100 text-green-700',
  events: 'bg-orange-100 text-orange-700',
  feedback: 'bg-pink-100 text-pink-700',
  resources: 'bg-cyan-100 text-cyan-700',
  portland: 'bg-emerald-100 text-emerald-700',
};

export default function Forum() {
  const [threads, setThreads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const sort = searchParams.get('sort') || 'recent';
  const category = searchParams.get('category') || '';
  const tag = searchParams.get('tag') || '';
  const search = searchParams.get('search') || '';

  useEffect(() => {
    fetchThreads();
    fetchCategories();
    fetchTags();
  }, [sort, category, tag, search]);

  const fetchThreads = async () => {
    setIsLoading(true);
    try {
      const response = await threadAPI.getAll({ 
        sort, 
        category: category || undefined, 
        tag: tag || undefined, 
        search: search || undefined 
      });
      setThreads(response.data);
    } catch (error) {
      console.error('Failed to fetch threads:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await threadAPI.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await threadAPI.getTags();
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const searchValue = formData.get('search');
    const newParams = new URLSearchParams(searchParams);
    if (searchValue) {
      newParams.set('search', searchValue);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  const handleSortChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('sort', value);
    setSearchParams(newParams);
  };

  const handleCategoryChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('category', value);
    } else {
      newParams.delete('category');
    }
    setSearchParams(newParams);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="forum-page">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Community Forum</h1>
            <p className="text-slate-600 mt-1">Discuss ideas, ask questions, and connect with fellow hackers</p>
          </div>
          <Link to="/forum/create">
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-thread-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Thread
            </Button>
          </Link>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 mb-4">Categories</h3>
                <div className="space-y-1">
                  <button
                    onClick={() => handleCategoryChange('all')}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      !category ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-slate-100 text-slate-700'
                    }`}
                    data-testid="category-all"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span className="text-sm font-medium">All Discussions</span>
                  </button>
                  {categories.map((cat) => {
                    const Icon = CATEGORY_ICONS[cat.id] || MessageSquare;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => handleCategoryChange(cat.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          category === cat.id ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-slate-100 text-slate-700'
                        }`}
                        data-testid={`category-${cat.id}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{cat.name}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Popular Tags */}
            {tags.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-slate-900 mb-4">Popular Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {tags.slice(0, 10).map((t) => (
                      <Badge
                        key={t.tag}
                        variant="secondary"
                        className="cursor-pointer hover:bg-indigo-100"
                        onClick={() => {
                          const newParams = new URLSearchParams(searchParams);
                          newParams.set('tag', t.tag);
                          setSearchParams(newParams);
                        }}
                      >
                        {t.tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    name="search"
                    placeholder="Search discussions..."
                    defaultValue={search}
                    className="pl-10 bg-white"
                    data-testid="forum-search-input"
                  />
                </div>
              </form>
              
              <Select value={sort} onValueChange={handleSortChange}>
                <SelectTrigger className="w-[180px] bg-white" data-testid="forum-sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="popular">Most Popular</SelectItem>
                  <SelectItem value="active">Most Active</SelectItem>
                  <SelectItem value="unanswered">Unanswered</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Threads List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : threads.length === 0 ? (
              <div className="text-center py-20">
                <MessageSquare className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No discussions yet</h3>
                <p className="text-slate-600 mb-6">Start the conversation!</p>
                <Link to="/forum/create">
                  <Button className="bg-emerald-600">Start Discussion</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {threads.map((thread, index) => {
                  const CategoryIcon = CATEGORY_ICONS[thread.category] || MessageSquare;
                  return (
                    <div
                      key={thread.thread_id}
                      className={`post-card animate-fade-in-up stagger-${(index % 5) + 1}`}
                      data-testid={`thread-card-${thread.thread_id}`}
                    >
                      {/* Vote Column */}
                      <div className="flex flex-col items-center justify-center px-3 py-4 bg-slate-50 border-r border-slate-200">
                        <VoteButtons
                          targetType="thread"
                          targetId={thread.thread_id}
                          initialUpvotes={thread.upvotes}
                          initialDownvotes={thread.downvotes}
                          userVote={thread.user_vote}
                          size="sm"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-4">
                        {/* Status badges */}
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={CATEGORY_COLORS[thread.category] || 'bg-slate-100 text-slate-700'}>
                            <CategoryIcon className="w-3 h-3 mr-1" />
                            {thread.category}
                          </Badge>
                          {thread.is_pinned && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              <Pin className="w-3 h-3 mr-1" />
                              Pinned
                            </Badge>
                          )}
                          {thread.is_locked && (
                            <Badge variant="outline" className="text-red-600 border-red-300">
                              <Lock className="w-3 h-3 mr-1" />
                              Locked
                            </Badge>
                          )}
                          {thread.is_solved && (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Solved
                            </Badge>
                          )}
                        </div>

                        {/* Title */}
                        <Link to={`/forum/thread/${thread.thread_id}`}>
                          <h3 className="text-lg font-semibold text-slate-900 hover:text-emerald-600 transition-colors mb-2">
                            {thread.title}
                          </h3>
                        </Link>

                        {/* Preview */}
                        <p className="text-slate-600 text-sm line-clamp-2 mb-3">
                          {thread.content}
                        </p>

                        {/* Footer */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <Link to={`/users/${thread.author_id}`} className="flex items-center gap-1.5 hover:text-slate-700">
                              <Avatar className="w-5 h-5">
                                <AvatarImage src={thread.author_picture} />
                                <AvatarFallback className="text-[10px] bg-emerald-100 text-emerald-700">
                                  {thread.author_name?.charAt(0)?.toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium">{thread.author_name}</span>
                            </Link>
                            <span>•</span>
                            <span>{formatDistanceToNow(new Date(thread.created_at), { addSuffix: true })}</span>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            {thread.tags?.length > 0 && (
                              <div className="hidden sm:flex gap-1">
                                {thread.tags.slice(0, 2).map((t) => (
                                  <Badge key={t} variant="secondary" className="text-xs">
                                    {t}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-4 h-4" />
                              {thread.reply_count}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
